<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user'
import type { UserAttrType } from '@/types/user'
import { formatNumber } from '@/utils'
import { QuestionFilled } from '@element-plus/icons-vue'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    userAttr?: UserAttrType
    showType?: string
    showBorder?: boolean
    showTitle?: boolean
  }>(),
  {
    showType: '',
    showBorder: true,
    showTitle: true,
  },
)
const userStore = useUserStore()

const userAttr = computed(() => {
  return props.userAttr || userStore.userAttr
})
</script>

<template>
  <div class="user-attrs" :class="{ 'border-wrap': showBorder }">
    <h4 v-if="showTitle">
      基本属性
      <div class="help-info" v-if="showType === 'relic'">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="16"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>圣物说明</h4>
            <p>1. 装备<span class="relic">圣物</span>可获得其全部的属性加成。</p>
            <p>2. 圣物具备<span class="info">成长能力</span>，反复获得的圣物将获得额外的属性加成，最高 <span class="grow">50</span>级。</p>
            <p>3. 部分圣物具备<span class="special">改变攻击元素</span>的能力</p>
            <p>4. <span class="info">元素伤害加成</span>仅在攻击元素改变时生效。</p>
            <p>5. 携带多种改变攻击元素的圣物时，仅生效最高加成的元素。</p>
          </div>
        </el-popover>
      </div>
      <div class="help-info" v-if="showType === 'skill'">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="16"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>技能说明</h4>
            <p>1. 装备<span class="mp">技能书</span>以支持释放技能。</p>
            <p>2. 装备时，将根据<span class="info">冷却时间</span>自动排序。</p>
            <p>
              3. 战斗时，<span class="grow">优先释放</span>排序在前的主动技能：只有排序在前的技能冷却时，才会释放排序在其后的技能；当<span class="mp"
                >魔法值</span
              >不足以释放<span class="info">排序在前且已完成冷却</span>的技能时，将停下等待。
            </p>
            <p>4. 技能<span class="info">词缀</span>仅在释放时生效。</p>
          </div>
        </el-popover>
      </div>
    </h4>
    <div class="text-wrap">
      <p>
        <span>攻击：</span><span>{{ userStore.userAttr.atk }}</span>
      </p>
      <p>
        <span>攻击速度：</span><span>{{ (userStore.userAttr.atksp / 100).toFixed(3) }}</span>
      </p>
      <p>
        <span>暴击率：</span><span>{{ userStore.userAttr.crt }}%</span>
      </p>
      <p>
        <span>暴击伤害：</span>
        <span style="text-align: right">
          <span :class="{ 'be-shrink': userStore.soulType > 3 && userStore.userAttr.crtd !== userStore.getRealCrtd() }">
            {{ userStore.userAttr.crtd }}%
          </span>
          <span v-if="userStore.soulType > 3 && userStore.userAttr.crtd !== userStore.getRealCrtd()">
            {{ formatNumber(userStore.getRealCrtd(), 2) }}%
          </span>
        </span>
      </p>
      <p>
        <span>破防：</span><span>{{ userStore.userAttr.heat }}</span>
      </p>
      <p>
        <span>命中率：</span><span>{{ userStore.userAttr.hr }}%</span>
      </p>
      <p>
        <span>抗魔值：</span><span>{{ formatNumber(userStore.userAttr.voidDef, 2) }}</span>
      </p>
      <p>
        <span>全伤害加成：</span><span>{{ formatNumber(userStore.userAttr.ad, 2) }}%</span>
      </p>

      <template v-if="userStore.soulType > 3">
        <p>
          <span>魔法值：</span><span>{{ userStore.userAttr.mp }}</span>
        </p>
        <p>
          <span>魔法回复：</span><span>{{ userStore.userAttr.mpRegen }}</span>
        </p>
        <p>
          <span>冷却缩减：</span>
          <span style="text-align: right">
            <span>{{ formatNumber(userStore.userAttr.cda, 2) }} ({{ formatNumber(userStore.getRealSpecAttr(userStore.userAttr.cda), 2) }}%)</span>
          </span>
        </p>
        <p>
          <span>法耗缩减：</span>
          <span style="text-align: right">
            <span>{{ formatNumber(userStore.userAttr.sce, 2) }} ({{ formatNumber(userStore.getRealSpecAttr(userStore.userAttr.sce), 2) }}%)</span>
          </span>
        </p>
        <p>
          <span>技能伤害加成：</span><span>{{ formatNumber(userStore.userAttr.skillDa, 2) }}%</span>
        </p>
      </template>
      <template v-if="userStore.advanceLevel === 4">
        <p>
          <span>风伤害加成：</span><span>{{ formatNumber(userStore.userAttr.windDa, 2) }}%</span>
        </p>
        <p>
          <span>火伤害加成：</span><span>{{ formatNumber(userStore.userAttr.fireDa, 2) }}%</span>
        </p>
        <p>
          <span>水伤害加成：</span><span>{{ formatNumber(userStore.userAttr.waterDa, 2) }}%</span>
        </p>
        <p>
          <span>土伤害加成：</span><span>{{ formatNumber(userStore.userAttr.soilDa, 2) }}%</span>
        </p>
      </template>
      <template v-if="userStore.soulType > 3">
        <el-popover placement="bottom" width="50vw" popper-class="see-attr" trigger="click">
          <template #reference>
            <el-button type="primary" plain size="small" style="width: 100%">更多属性</el-button>
          </template>
          <div class="border-wrap" style="padding: 0 10px; max-width: 50vh; max-height: 30vh; overflow: auto">
            <p>
              <span>独立风伤害加成：</span><span>{{ formatNumber(userStore.userAttr.windIndDa) }}%</span>
            </p>
            <p>
              <span>独立火伤害加成：</span><span>{{ formatNumber(userStore.userAttr.fireIndDa) }}%</span>
            </p>
            <p>
              <span>独立水伤害加成：</span><span>{{ formatNumber(userStore.userAttr.waterIndDa) }}%</span>
            </p>
            <p>
              <span>独立土伤害加成：</span><span>{{ formatNumber(userStore.userAttr.soilIndDa) }}%</span>
            </p>
            <p>
              <span>全技能等级加成：</span><span>{{ userStore.userAttr.allSkillGrow }}</span>
            </p>
            <p>
              <span>风系技能等级加成：</span><span>{{ userStore.userAttr.windSkillGrow }}</span>
            </p>
            <p>
              <span>火系技能等级加成：</span><span>{{ userStore.userAttr.fireSkillGrow }}</span>
            </p>
            <p>
              <span>水系技能等级加成：</span><span>{{ userStore.userAttr.waterSkillGrow }}</span>
            </p>
            <p>
              <span>土系技能等级加成：</span><span>{{ userStore.userAttr.soilSkillGrow }}</span>
            </p>
            <p>
              <span>对不死族伤害加成：</span><span>{{ formatNumber(userStore.userAttr.undeadDa, 2) }}%</span>
            </p>
            <p>
              <span>对兽族伤害加成：</span><span>{{ formatNumber(userStore.userAttr.orcDa, 2) }}%</span>
            </p>
            <p>
              <span>对穴居族伤害加成：</span><span>{{ formatNumber(userStore.userAttr.caveDa, 2) }}%</span>
            </p>
            <p>
              <span>对元素族伤害加成：</span><span>{{ formatNumber(userStore.userAttr.elDa, 2) }}%</span>
            </p>
            <p>
              <span>对精灵伤害加成：</span><span>{{ formatNumber(userStore.userAttr.elfDa, 2) }}%</span>
            </p>
            <p>
              <span>对恶魔伤害加成：</span><span>{{ formatNumber(userStore.userAttr.dmDa, 2) }}%</span>
            </p>
            <p>
              <span>对妖精伤害加成：</span><span>{{ formatNumber(userStore.userAttr.faeDa, 2) }}%</span>
            </p>
            <p>
              <span>对泥泽族伤害加成：</span><span>{{ formatNumber(userStore.userAttr.mzDa, 2) }}</span>
            </p>
          </div>
        </el-popover>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.user-attrs {
  max-height: 208px;
  display: flex;
  flex-flow: column nowrap;
  overflow: hidden;
  padding: 0 8px;
  h4 {
    position: relative;
    .help-info {
      position: absolute;
      top: 2px;
      right: 10px;
    }
  }
  .text-wrap {
    overflow: auto;
    p {
      font-size: 14px;
      margin: 4px 0;
      display: flex;
      justify-content: space-between;
    }
    .be-shrink {
      text-decoration: line-through;
      color: #7d7d7d;
      font-size: 12px;
    }
  }
}
</style>
